<script lang="ts">
  import { FREE_INDEX, type PlayerView } from '@banger/shared';
  import { send, lastError } from '../lib/connection';
  import Avatar from '../shared/Avatar.svelte';

  type Target = NonNullable<PlayerView['me']['canSteal']>[number];

  let { targets, round, secs }: {
    /** `me.canSteal` while the steal is unused; null once it has been sent. */
    targets: Target[] | null;
    round: number;
    secs: number;
  } = $props();

  let chosen = $state<Target | null>(null);
  let sent = $state<{ name: string; emoji: string } | null>(null);
  let sentAt = $state(0);
  let now = $state(Date.now());
  $effect(() => {
    if (!sentAt) return;
    const t = setTimeout(() => (now = Date.now()), 1600);
    return () => clearTimeout(t);
  });
  // Rejected (the steal is still offered): let them try again.
  $effect(() => { if ($lastError && targets !== null) sent = null; });
  // Confirmed once the offer leaves the view; unconfirmed after 1.5 s (lost send): offer it again.
  const stolen = $derived(sent && (targets === null || now - sentAt < 1500) ? sent : null);

  function steal(cell: number) {
    if (!chosen || stolen) return;
    send({ type: 'steal', round, target: chosen.id, cell });
    sent = { name: chosen.name, emoji: chosen.emoji };
    sentAt = Date.now();
    now = sentAt;
    navigator.vibrate?.([40, 40, 80]);
  }
</script>

<section>
  {#if stolen}
    <p class="head">😈 Stolen!</p>
    <p class="sub">{stolen.emoji} {stolen.name} loses that square.</p>
  {:else if targets && targets.length === 0}
    <p class="head">🎯 Exact year!</p>
    <p class="sub">No squares to steal 😅</p>
  {:else if targets && chosen}
    <p class="head">Steal from {chosen.emoji} {chosen.name}</p>
    <p class="sub">Tap one of their glowing squares · {secs}s</p>
    <div class="mini" role="grid" aria-label="{chosen.name}'s card">
      {#each Array.from({ length: 25 }, (_, i) => i) as i (i)}
        {#if chosen.cells.includes(i)}
          <button type="button" class="sq hot" aria-label="Steal square {i + 1}" onclick={() => steal(i)}>✓</button>
        {:else}
          <div class="sq" class:free={i === FREE_INDEX} role="gridcell">{i === FREE_INDEX ? '★' : ''}</div>
        {/if}
      {/each}
    </div>
    <button type="button" class="back" onclick={() => (chosen = null)}>← Pick someone else</button>
  {:else if targets}
    <p class="head">😈 Now steal a square!</p>
    <p class="sub">Pick a player · {secs}s</p>
    <div class="players">
      {#each targets as t (t.id)}
        <button type="button" class="player" onclick={() => (chosen = t)}>
          <Avatar emoji={t.emoji} size={44} />
          <span class="name">{t.name}</span>
          <span class="count">{t.cells.length} ✓</span>
        </button>
      {/each}
    </div>
  {/if}
</section>

<style>
  section { display: grid; gap: 10px; width: 100%; max-width: 420px; margin: 0 auto; }
  button { touch-action: manipulation; font: inherit; cursor: pointer; }
  .head { margin: 0; text-align: center; font-size: 1.5rem; font-weight: 900; color: #fff; text-shadow: 0 0 10px var(--pink), 0 0 24px var(--pink); }
  .sub { margin: 0; text-align: center; opacity: 0.8; }
  .players { display: grid; gap: 8px; }
  .player {
    display: flex; align-items: center; gap: 12px; min-height: 60px; padding: 6px 16px 6px 8px; border-radius: 999px;
    color: #fff; background: rgba(255, 61, 242, 0.08); border: 2px solid var(--pink); box-shadow: 0 0 12px rgba(255, 61, 242, 0.35);
  }
  .player:active { transform: scale(0.97); }
  .name { flex: 1; text-align: left; font-size: 1.2rem; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .count { opacity: 0.75; font-weight: 700; }
  .mini { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; }
  .sq {
    aspect-ratio: 1; min-width: 0; min-height: 0; border-radius: 10px; display: flex; align-items: center; justify-content: center;
    border: 2px solid rgba(255, 255, 255, 0.15); background: rgba(10, 6, 24, 0.5); color: rgba(255, 255, 255, 0.4); font-weight: 900; padding: 0;
  }
  .sq.free { background: rgba(255, 255, 255, 0.2); color: #0a0618; }
  .hot {
    border-color: var(--pink); background: var(--pink); color: #0a0618; font-size: 1.4rem;
    box-shadow: 0 0 14px var(--pink); animation: throb 0.8s ease-in-out infinite alternate;
  }
  .hot:active { transform: scale(0.9); }
  .back { min-height: 48px; border: 0; background: none; color: var(--cyan); font-weight: 700; }
  @keyframes throb { from { box-shadow: 0 0 6px var(--pink); } to { box-shadow: 0 0 24px var(--pink), 0 0 40px rgba(255, 61, 242, 0.5); } }
</style>
